import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";
import { useEffect, useRef, useState } from "react";
import ReCaptchaButton from "../ReCaptchaButton/ReCaptchaButton";
import localeText from "../../locale";
import GetInfoService from "../../service/GetInfoService";
import logoColor from "../../pages/images/logo-color-horizontal.png";
import SpecialCustomerService from "../../service/Cadastros/SpecialCustomerService";
import { useRegisterValidation } from "./hooks/useRegisterValidation";
import NameFields from "./components/NameFields";
import EmailField from "./components/EmailField";
import PasswordFields from "./components/PasswordFields";
import TermsAndMarketing from "./components/TermsAndMarketing";

const RegisterDialog = ({ visible, onHide, onRegisterSuccess, onOpenLogin }) => {
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [password, setPassword] = useState("");
  const [terms, setTerms] = useState(false);
  const [marketingEmail, setMarketingEmail] = useState(false);
  const [hasReferral, setHasReferral] = useState(false);
  const [referralUsername, setReferralUsername] = useState("");
  const [hasInviteCoupon, setHasInviteCoupon] = useState(false);
  const [inviteCoupon, setInviteCoupon] = useState("");
  const [selectedCurrency, setSelectedCurrency] = useState("EUR");
  const [referralIconSpinner, setReferralIconSpinner] = useState("");
  const [country, setCountry] = useState("");
  const [tokenRecaptcha, setTokenRecaptcha] = useState("");
  const [loading, setLoading] = useState(false);

  const referralRef = useRef(null);
  const emailRef = useRef(null);
  const toast = useRef(null);

  const getInfoService = new GetInfoService();
  const specialCustomerService = new SpecialCustomerService();
  const { validateEmail, findReferral } = useRegisterValidation(toast);

  let language = localStorage.getItem("__extraconomy_locale")
    ? localStorage.getItem("__extraconomy_locale")
    : navigator.language;

  switch (language) {
    case "pt":
      language = "PORTUGUESE";
      break;
    case "it":
      language = "ITALIAN";
      break;
    case "es":
      language = "SPANISH";
      break;
    default:
      language = "ENGLISH";
      break;
  }

  const getGuestCartFirstProductId = () => {
    const orderKey = "__extraconomy_order";
    const guestOrder = localStorage.getItem(orderKey);
    if (guestOrder) {
      const order = JSON.parse(guestOrder);
      if (order?.isGuestCart && order?.itens?.length > 0) {
        return order.itens[0]?.product?.id || null;
      }
    }
    return null;
  };

  useEffect(() => {
    if (!visible) {
      setEmail("");
      setFullName("");
      setPassword("");
      setTerms(false);
      setMarketingEmail(false);
      setHasReferral(false);
      setReferralUsername("");
      setHasInviteCoupon(false);
      setInviteCoupon("");
      setTokenRecaptcha("");
      setReferralIconSpinner("");
    }
  }, [visible]);

  useEffect(() => {
    if (visible) {
      const getInfoServiceInstance = new GetInfoService();
      getInfoServiceInstance.getUserCurrency().then((res) => setSelectedCurrency(res));
      getInfoServiceInstance.getUserCountry().then((res) => setCountry(res));
    }
  }, [visible]);

  const handleBlurReferral = (e) => {
    if (hasReferral && e.target.value) {
      findReferral(e.target.value, referralRef, setReferralIconSpinner);
    }
  };

  const handleBlurEmail = () => {
    validateEmail(email, emailRef);
  };

  const splitFullName = (fullName) => {
    const nameParts = fullName.trim().split(/\s+/);
    if (nameParts.length === 0 || fullName.trim() === "") {
      return { fname: "", lname: "" };
    }
    if (nameParts.length === 1) {
      return { fname: nameParts[0], lname: "" };
    }
    const lname = nameParts[nameParts.length - 1];
    const fname = nameParts.slice(0, -1).join(" ");
    return { fname, lname };
  };

  async function registerCustomer() {
    if (
      email.length === 0 ||
      fullName.trim().length === 0 ||
      password.length === 0 ||
      !terms
    ) {
      const errorDetail = localeText("pleaseFillOutAll");
      toast.current.show({
        severity: "error",
        summary: localeText("error"),
        detail: errorDetail || "Por favor preencha todos os campos obrigatórios",
        life: 6000,
      });
      return;
    }

    if (password.length < 8) {
      const errorDetail = localeText("passwordMin8");
      toast.current.show({
        severity: "error",
        summary: localeText("error"),
        detail: errorDetail || "A senha deve ter pelo menos 8 caracteres",
        life: 6000,
      });
      return;
    }

    if (!validateEmail(email, emailRef)) {
      return;
    }

    const finalReferral = hasReferral && referralUsername ? referralUsername : "extraconomy";

    if (hasReferral && referralUsername) {
      const referralValid = await findReferral(finalReferral, referralRef, setReferralIconSpinner);
      if (!referralValid) {
        return;
      }
    }

    if (hasInviteCoupon && !inviteCoupon.trim()) {
      toast.current.show({
        severity: "error",
        summary: localeText("error"),
        detail: localeText("inviteCouponRequired"),
        life: 6000,
      });
      return;
    }

    if (tokenRecaptcha.length === 0) {
      const errorDetail = localeText("pleaseReverify");
      toast.current.show({
        severity: "error",
        summary: localeText("error"),
        detail: errorDetail || "Por favor, complete a verificação ReCAPTCHA",
        life: 6000,
      });
      return;
    }

    try {
      setLoading(true);

      let location;
      if (!localStorage.getItem("__extraconomy_lat__")) {
        location = await getInfoService.getUserLocation();
      }

      const { fname, lname } = splitFullName(fullName);

      const idProductCart = getGuestCartFirstProductId();

      const res = await specialCustomerService.postRegisterSpecialCustomer(
        finalReferral,
        null,
        email,
        null,
        fname,
        lname,
        password,
        selectedCurrency,
        language,
        null,
        country,
        null,
        null,
        null,
        localStorage.getItem("__extraconomy_lat__")
          ? localStorage.getItem("__extraconomy_lat__")
          : location?.latitude,
        localStorage.getItem("__extraconomy_long__")
          ? localStorage.getItem("__extraconomy_long__")
          : location?.longitude,
        idProductCart
      );

      if (res.messageCode !== 200000) {
        const errorMessage = localeText("data").find((msg) => msg.code === res.messageCode);
        const errorDetail = errorMessage ? errorMessage.message : localeText("genericError");

        toast.current.show({
          severity: "error",
          summary: localeText("error"),
          detail: errorDetail || "Erro ao registrar. Tente novamente.",
          life: 6000,
        });
        setLoading(false);
      } else {
        setLoading(false);
        toast.current.show({
          severity: "success",
          summary: localeText("success"),
          detail: localeText("registrationSuccessActivateYourAccount"),
          life: 6000,
        });

        setTimeout(() => {
          if (onRegisterSuccess) {
            onRegisterSuccess();
          }
        }, 2000);
      }
    } catch (e) {
      setLoading(false);
      const errorDetail = localeText("genericError");

      toast.current.show({
        severity: "error",
        summary: localeText("error"),
        detail: errorDetail || `Erro ao registrar: ${e.message || "Erro desconhecido"}`,
        life: 6000,
      });
    }
  }

  return (
    <>
      <Toast position="top-left" ref={toast} />

      <Dialog
        visible={visible}
        onHide={onHide}
        dismissableMask={!loading}
        closable={!loading}
        draggable={false}
        breakpoints={{ "960px": "90vw", "640px": "95vw" }}
        style={{ width: "660px", maxWidth: "95vw", height: "770px" }}
        contentClassName="p-0"
        headerStyle={{ background: "#fff", paddingInline: "0.5rem" }}
        headerClassName="login-dialog-close-icon"
        closeIcon={
          <i
            className="pi pi-times"
            style={{
              color: "#B3BAC5",
              cursor: "pointer",
            }}
          />
        }
      >
        <div className="flex flex-column align-items-center gap-4 p-4 h-full" style={{ overflowY: "auto" }}>
          <img src={logoColor} alt="Extraconomy logo" style={{ height: "40px" }} />

          <div className="flex flex-column align-items-left gap-2 w-full" style={{ maxWidth: "360px" }}>
            <div className="text-3xl font-bold" style={{ color: "#181D27" }}>
              {localeText("createAccount")}
            </div>
            <div className="text-left text-sm" style={{ color: "#535862" }}>
              {localeText("youAreRegistering")} <b>CUSTOMER</b>
            </div>
          </div>

          <div className="flex flex-column gap-3 w-full" style={{ maxWidth: "360px" }}>
            <NameFields
              fullName={fullName}
              setFullName={setFullName}
              loading={loading}
            />

            <EmailField
              email={email}
              setEmail={setEmail}
              emailRef={emailRef}
              onBlurEmail={handleBlurEmail}
              loading={loading}
            />

            <PasswordFields
              password={password}
              setPassword={setPassword}
              loading={loading}
            />

            <TermsAndMarketing
              terms={terms}
              setTerms={setTerms}
              marketingEmail={marketingEmail}
              setMarketingEmail={setMarketingEmail}
              hasReferral={hasReferral}
              setHasReferral={setHasReferral}
              referralUsername={referralUsername}
              setReferralUsername={setReferralUsername}
              referralIconSpinner={referralIconSpinner}
              setReferralIconSpinner={setReferralIconSpinner}
              referralRef={referralRef}
              onBlurReferral={handleBlurReferral}
              hasInviteCoupon={hasInviteCoupon}
              setHasInviteCoupon={setHasInviteCoupon}
              inviteCoupon={inviteCoupon}
              setInviteCoupon={setInviteCoupon}
              loading={loading}
            />

            <ReCaptchaButton token={tokenRecaptcha} setToken={setTokenRecaptcha} />

            <div>
              <Button
                label={localeText("createFreeAccount")}
                loading={loading}
                onClick={registerCustomer}
                style={{ backgroundColor: "#28AB69" }}
                className="w-full font-semibold text-base text-white p-no-outline border-none px-4 py-3 border-round-lg"
              />
            </div>
          </div>

          <div className="text-sm text-center" style={{ color: "#344563" }}>
            {localeText("alreadyHaveAccount")}{" "}
            <span
              className="font-semibold cursor-pointer"
              style={{ color: "#245AE5" }}
              onClick={() => {
                if (!loading && onOpenLogin) {
                  onOpenLogin();
                }
              }}
            >
              {localeText("doLogin")}
            </span>
          </div>

          <div className="text-center text-sm mt-auto" style={{ color: "#7A869A" }}>
            © Extraconomy - 2025
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default RegisterDialog;
